/**
 * useDocumentRows — TanStack Query hook for the Ablage grid (dms/spec.md §M11).
 *
 * Always fetches the BROAD window (`include_inactive: true`, no `state` filter)
 * — the State/Kategorie/Quelle pills then filter CLIENT-SIDE over that one
 * fetch, exactly like `usePolicyRows`' Alle/Aktiv/Renewal/Bald-fällig pills and
 * `AufgabenScreen`'s saved-view presets. Only the fulltext `q` (filename +
 * `extracted_text_tsv` — content the client can't see) is sent to the server,
 * debounced (the `useSearch` precedent) so keystrokes don't each fire a request.
 *
 * live mode → GET /documents via fetchDocumentList (paginated envelope).
 * mock mode → the DOCUMENTS_MOCK fixture, `q`-filtered the same way.
 *
 * [Source: apps/web/src/features/policies/hooks/usePolicyRows.ts] (broad-fetch + client pills)
 * [Source: apps/web/src/features/search/hooks/useSearch.ts] (debounce precedent)
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DocumentListItemDto } from '@brokly/shared';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { fetchDocumentList } from '../api/documents-api';
import { DOCUMENTS_MOCK } from '../mock';

/** Debounce window (ms) between the last keystroke and the `q` request (useSearch precedent). */
export const DOCUMENTS_SEARCH_DEBOUNCE_MS = 250;

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function mockList(q: string): { items: DocumentListItemDto[]; totalCount: number | null } {
  const rows = q ? DOCUMENTS_MOCK.filter((r) => r.title.toLowerCase().includes(q.toLowerCase())) : DOCUMENTS_MOCK;
  return { items: rows, totalCount: rows.length };
}

export function useDocumentRows(rawQuery = '') {
  const q = useDebounced(rawQuery.trim(), DOCUMENTS_SEARCH_DEBOUNCE_MS);
  const live = isLiveData();

  const query = useQuery({
    queryKey: qk.documents({ q, include_inactive: true, mode: live ? 'live' : 'mock' }),
    queryFn: () =>
      live
        ? fetchDocumentList({ q: q || undefined, include_inactive: true })
        : Promise.resolve(mockList(q)),
    ...(live ? {} : { initialData: () => mockList(q), staleTime: Infinity }),
  });

  return {
    rows: query.data?.items ?? [],
    totalCount: query.data?.totalCount ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
